import { Component, EventEmitter, Input, OnDestroy, Output, SimpleChanges } from '@angular/core';
import * as Highcharts from 'highcharts';
import { timer, Subscription, fromEvent } from 'rxjs';
import * as moment from 'moment';
import { Log } from 'src/app/logger.service';
import { getPeopleColumnByName } from 'src/app/people/people-columns';
import {
  QuickBuild,
  UniqueIdGenerator,
  QuickBuildService,
  UIFilter,
  UIFilterGroup,
} from 'src/app/people/quick-build.service';
import { PeopleReportService, ReportResponse } from 'src/app/people/report-service.service';
import { StreamsSubscribeDrawerService } from '../streams-subscribe-drawer/streams-subscribe-drawer.service';
import { AuthService } from 'src/app/auth/service/auth.service';
import { StatsigService } from '@statsig/angular-bindings';
import { HeapUtils } from 'src/app/api-client/heap.utils';
import { WorkforceLayoutService } from 'src/app/workforce/workforce-layout.service';

export interface ChartContextMenuItem {
  label?: string;
  action?: (point: Highcharts.Point) => void;
  icon?: string;
  disabled?: boolean;
  divider?: boolean;
}

export interface ReportResponseWithName {
  name: string;
  data: ReportResponse;
}
@Component({
  template: '',
  standalone: false,
})
export abstract class BaseReportComponent<TConfig = any> implements OnDestroy {
  @Input() quickBuild: QuickBuild;
  @Input() reportTarget: string;
  @Input() reportId: string;
  @Output() configChange = new EventEmitter<TConfig>();

  protected chart: Highcharts.Chart;
  protected chartId: string;
  protected subscriptions: Subscription = new Subscription();
  protected contextMenu: HTMLElement | null = null;
  private documentClickSubscription: Subscription;
  private layoutSubscription: Subscription;
  private chartInitializationPromise: Promise<void> | null = null;
  private isInitializing: boolean = false;

  // Error overlay properties
  protected defaultErrorMessage: string = 'There was an error loading chart data';
  protected errorOverlay: HTMLElement | null = null;
  protected loadingOverlay: HTMLElement | null = null;
  protected noDataOverlay: HTMLElement | null = null;

  // Configuration management
  private configProperties: Map<string, () => any> = new Map();
  private _config: Partial<TConfig> = {};

  protected abstract readonly CHART_ID: string;
  protected readonly CHART_DATA_DESC: string; // // TODO : make this abstract so all charts have a description
  protected rawResponses: any; // TODO : make this abstract so all charts have a raw response
  protected abstract getContextMenuItems(point: Highcharts.Point): ChartContextMenuItem[];

  protected showChartOptions: boolean = true;

  /**
   * Finds the chart container element with retry logic and exponential backoff
   * @returns Promise that resolves to the chart container element or null if not found
   */
  protected findChartContainer(
    maxAttempts: number = 10,
    maxDelay: number = 1000
  ): Promise<HTMLElement | null> {
    const containerId = `highchartsContainer-${this.chartId}`;

    // Try to find it immediately
    const immediateResult = document.getElementById(containerId);
    if (immediateResult) {
      return Promise.resolve(immediateResult);
    }

    return new Promise((resolve) => {
      let attempts = 0;

      const attemptFind = () => {
        const chartContainer = document.getElementById(containerId);

        if (chartContainer) {
          resolve(chartContainer);
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          const delay = Math.min(100 * Math.pow(2, attempts - 1), maxDelay);
          setTimeout(attemptFind, delay); // automatically cleaned up by garbage collector after callback executes, no need to unsubscribe manually (unlike rxjs timers)
        } else {
          Log.s('BaseReportComponent findChartContainer error', 'error', {
            chart: this.CHART_ID,
          });
          resolve(null);
        }
      };

      attemptFind();
    });
  }

  constructor(
    protected reportService: PeopleReportService,
    protected quickBuildService: QuickBuildService,
    private streamsDrawerService: StreamsSubscribeDrawerService,
    private auth: AuthService,
    private statsigService: StatsigService,
    protected layoutService: WorkforceLayoutService
  ) {
    this.chartId = UniqueIdGenerator.generate();
    if (!this.reportId) {
      this.reportId = UniqueIdGenerator.generate();
    }
  }

  /**
   * Creates a promise that resolves when the chart is initialized
   * Handles waiting for DOM element and retry logic with exponential backoff
   */
  private initializeChartWithPromise(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      requestAnimationFrame(() => {
        const maxAttempts = 10;
        let attempts = 0;

        const attemptInitialize = async () => {
          try {
            // First ensure the DOM element exists
            const chartContainer = await this.findChartContainer(maxAttempts, 1000);
            if (!chartContainer) {
              throw new Error(`Chart container not found for ${this.chartId}`);
            }

            // Now initialize the chart
            this.initializeChart();

            // Verify chart was created successfully
            if (!this.chart) {
              throw new Error('Chart initialization returned but chart object is null');
            }

            this.initLayoutWatcherForSettings();

            // Set initial chart size to match container
            setTimeout(() => {
              if (this.chart && chartContainer.parentElement) {
                const containerHeight = chartContainer.parentElement.offsetHeight;
                this.chart.setSize(undefined, containerHeight, false);
              }
            }, 100);

            // Resolve the promise - chart is ready
            resolve();
          } catch (error) {
            attempts++;
            if (attempts < maxAttempts) {
              // Exponential backoff: 100ms, 200ms, 400ms, etc. up to 10 attempts
              const delay = Math.min(100 * Math.pow(2, attempts - 1), 1000);
              timer(delay).subscribe(() => {
                attemptInitialize();
              });
            } else {
              Log.s('BaseReportComponent initializeChartWithPromise error', 'error', {
                chart: this.CHART_ID,
                error: error?.toString() || 'Unknown error',
              });
              this.showChartError(undefined, 'Failed to initialize chart. Please try again.');
              reject(error);
            }
          }
        };

        attemptInitialize();
      });
    });
  }

  /**
   * Ensures the chart is initialized before performing operations
   * Handles re-initialization if chart was destroyed (e.g., by *ngIf)
   */
  private async ensureChartInitialized(): Promise<void> {
    // Check if chart exists AND its DOM container still exists
    const chartContainer = document.getElementById(`highchartsContainer-${this.chartId}`);

    if (this.chart && chartContainer) {
      // Chart exists and DOM is present - we're good
      return;
    }

    // If already initializing, wait for that to complete
    if (this.isInitializing && this.chartInitializationPromise) {
      Log.d('Chart initialization already in progress, waiting...');
      await this.chartInitializationPromise;
      return;
    }

    // Start initialization
    this.isInitializing = true;

    try {
      // Chart is missing or DOM was destroyed - need to (re)initialize
      if (this.chart && !chartContainer) {
        // DOM was destroyed but chart object still exists - clean it up
        Log.d('Chart DOM destroyed, destroying chart object before re-init');
        try {
          this.chart.destroy();
        } catch (error) {
          Log.d(
            'BaseReportComponent ensureChartInitialzied: Error destroying orphaned chart',
            error
          );
        }
        this.chart = null as any;
      }

      // Clear old promise since we're creating a new one
      this.chartInitializationPromise = null;

      // Create and await new initialization
      this.chartInitializationPromise = this.initializeChartWithPromise();
      await this.chartInitializationPromise;
    } finally {
      this.isInitializing = false;
    }
  }

  initLayoutWatcherForSettings() {
    // If there are no config properties, we don't need to show the settings button
    if (this.configProperties.size === 0) {
      return;
    }

    const currentlySmall = this.layoutService.isSmallScreen;
    this.showChartOptions = !currentlySmall;

    // Only update chart if it exists
    if (this.chart) {
      this.chart.update({
        exporting: {
          buttons: {
            settingsButton: {
              enabled: currentlySmall,
              symbol: 'settings',
              x: -60,
              y: -5,
              onclick: () => {
                this.showChartOptions = !this.showChartOptions;
              },
            },
          },
        },
      });
    }

    // Clean up existing layout subscription if it exists
    if (this.layoutSubscription) {
      this.layoutSubscription.unsubscribe();
    }

    this.layoutSubscription = this.layoutService.isSmallScreen$.subscribe({
      next: (isSmall) => {
        this.showChartOptions = !isSmall;
        // Only update chart if it exists
        if (this.chart) {
          this.chart.update({
            exporting: {
              buttons: {
                settingsButton: {
                  enabled: isSmall,
                },
              },
            },
          });
        }
      },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.shouldRefreshOnChanges(changes)) {
      this.handleChanges();
    }
  }

  private async handleChanges(): Promise<void> {
    try {
      // Ensure chart is initialized (handles re-init if destroyed by *ngIf)
      await this.ensureChartInitialized();
      await this.resetChartData();
      this.getChartData();
    } catch (error) {
      Log.d('BaseReportComponent handleChanges: Chart initialization failed in ngOnChanges');
    }
  }

  protected abstract initializeChart(): void;
  protected abstract generateChartSeries(...args: any[]): any;
  protected abstract getChartData(): void;

  protected shouldRefreshOnChanges(changes: SimpleChanges): boolean {
    // Check standard properties that should trigger refresh
    const standardProperties = [
      'quickBuild',
      'quickBuilds',
      'reportTarget',
      'reportId',
      'dateFrom',
      'dateTo',
      'field',
    ];

    if (standardProperties.some((prop) => prop in changes)) {
      return true;
    }

    // Check all registered config properties
    for (const [propertyName] of this.configProperties) {
      if (propertyName in changes) {
        return true;
      }
    }

    return false;
  }

  protected async resetChartData() {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    await this.removeAllSeriesFromChart();
    this.hideChartError();
    this.hideNoData();
    this.showLoading();
  }

  protected async removeAllSeriesFromChart() {
    // Ensure chart is initialized before removing series
    try {
      await this.ensureChartInitialized();
    } catch (error) {
      // Chart failed to initialize, nothing to remove
      return;
    }

    if (!this.chart) return;
    while (this.chart.series.length > 0) {
      this.chart.series[0].remove(false);
    }
    this.chart.update({ subtitle: { text: undefined } });
  }

  protected hideLoading(): void {
    if (this.loadingOverlay) {
      this.loadingOverlay.remove();
      this.loadingOverlay = null;
    }
  }

  protected async chartLoadingComplete(): Promise<void> {
    this.hideLoading();

    // Ensure chart is initialized before checking for data
    try {
      await this.ensureChartInitialized();
    } catch (error) {
      // Don't show no data message if chart failed to initialize
      return;
    }

    if (
      this.chart &&
      (this.chart.series.length === 0 || this.chart.series.every((s) => s.data.length === 0))
    ) {
      this.showNoData();
    } else {
      this.hideNoData();
    }
  }

  protected showLoading(): void {
    this.hideLoading();

    const overlay = document.createElement('div');
    overlay.className = `
      chart-loading-overlay
      tw-absolute tw-top-1/2 tw-left-1/2
      tw-transform -tw-translate-x-1/2 -tw-translate-y-1/2
      tw-flex tw-flex-col tw-justify-center tw-items-center
      tw-z-10 tw-bg-gray-200 dark:tw-bg-gray-700 tw-rounded-lg tw-p-3
    `.replace(/\s+/g, ' ');

    overlay.innerHTML = `
      <div class="spinner"></div>
      <div class="tw-text-sm tw-text-dark-light-dynamic tw-font-semibold tw-mt-[50px]">Loading data...</div>
    `;

    // if there's an existing loading overlay, remove it
    if (this.loadingOverlay) {
      return;
    }

    this.loadingOverlay = overlay;

    this.findChartContainer().then((chartContainer) => {
      // Check if overlay still exists and hasn't been destroyed
      if (!chartContainer || !this.loadingOverlay || this.loadingOverlay !== overlay) {
        return;
      }

      chartContainer.appendChild(overlay);
    });
  }

  protected showNoData(): void {
    this.hideNoData();

    const overlay = document.createElement('div');
    overlay.className = `
      chart-no-data-overlay
      tw-absolute tw-top-1/2 tw-left-1/2
      tw-transform -tw-translate-x-1/2 -tw-translate-y-1/2
      tw-flex tw-flex-col tw-justify-center tw-items-center
      tw-z-10 tw-bg-gray-200 dark:tw-bg-gray-700 tw-rounded-lg tw-p-3
    `.replace(/\s+/g, ' ');

    overlay.innerHTML = `
      <div class="tw-font-semibold tw-my-1 tw-text-base tw-text-dark-light-dynamic">No data matches your filters</div>
    `;

    this.noDataOverlay = overlay;

    this.findChartContainer().then((chartContainer) => {
      // Check if overlay still exists and hasn't been destroyed
      if (!chartContainer || !this.noDataOverlay || this.noDataOverlay !== overlay) {
        return;
      }

      chartContainer.appendChild(overlay);
    });
  }

  protected hideNoData(): void {
    if (this.noDataOverlay) {
      this.noDataOverlay.remove();
      this.noDataOverlay = null;
    }
  }

  protected hideChartError(): void {
    if (this.errorOverlay) {
      this.errorOverlay.remove();
      this.errorOverlay = null;
    }
  }

  protected showChartError(error?: any, message?: string): void {
    let errorMessage = this.defaultErrorMessage;
    if (error && error.status === 429) {
      errorMessage = 'Too many requests. Wait a few seconds and try again.';
    }
    if (this.auth.palModeValue) {
      errorMessage = 'The link you used may be out of date. Please login to see all insights.';
    }
    if (message) {
      errorMessage = message;
    }

    let issueTitle = `BaseReportComponent showChartError ${this.CHART_ID}`;
    if (errorMessage !== this.defaultErrorMessage) {
      issueTitle += `: ${errorMessage}`;
    }
    const tags: Record<string, string> = {
      chart: this.CHART_ID,
      errorMessage: errorMessage,
      error: error?.message || error?.error || error?.toString(),
    };
    Log.s(issueTitle, 'error', tags);

    this.hideLoading();
    this.hideNoData();
    this.hideChartError();

    const overlay = document.createElement('div');
    overlay.className = `
      chart-error-overlay tw-bg-white dark:tw-bg-gray-800
      tw-absolute tw-inset-0
      tw-flex tw-flex-col tw-justify-center tw-items-center
      tw-z-20 tw-rounded-lg tw-p-6 tw-text-center
    `.replace(/\s+/g, ' ');

    overlay.innerHTML = `
      <span class="material-icons tw-text-[36px] tw-text-red-500 tw-mb-4">error_outline</span>
      <div class="tw-text-red-600 dark:tw-text-red-500 tw-font-medium tw-mb-4">${errorMessage}</div>
      <button class="chart-error-retry-btn tw-mt-2 tw-px-4 tw-py-2 tw-border tw-border-solid tw-border-link-color-dynamic-darker tw-text-link-color-dynamic tw-bg-transparent tw-rounded tw-font-medium dark:hover:tw-bg-teal-100/15 dark:active:tw-bg-teal-100/15 hover:tw-bg-indigo-200/20 active:tw-bg-indigo-200/20 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-link-color-dynamic-darker tw-transition-all">
        Retry
      </button>
    `;

    this.errorOverlay = overlay;

    // since this overlay has a btn, we need to add event listener to it (can't put it in the innerHTML directly)
    const retryBtn = overlay.querySelector('.chart-error-retry-btn');
    if (retryBtn) {
      retryBtn.addEventListener('click', () => {
        this.hideChartError();
        this.resetChartData();
        this.getChartData();
      });
    }

    this.findChartContainer().then((chartContainer) => {
      // Check if overlay still exists and hasn't been destroyed
      if (!chartContainer || !this.errorOverlay || this.errorOverlay !== overlay) {
        return;
      }

      chartContainer.appendChild(overlay);
    });
  }

  protected async updateChart(
    series: any[],
    categories?: any[],
    title?: string | undefined,
    options?: Highcharts.Options
  ) {
    // Hide loading when updating chart with data - this lets the chart start drawing whatever data it has
    this.hideLoading();
    this.hideNoData();
    this.hideChartError();

    let valueToSet: Highcharts.Options = {
      series,
      xAxis: categories ? { categories } : undefined,
    };

    if (title) {
      valueToSet.title = { text: title };
    }

    valueToSet.subtitle = this.getSubtitleOptions();

    if (options) {
      valueToSet = Highcharts.merge(valueToSet, options);
    }

    // Ensure chart is initialized before updating
    try {
      await this.ensureChartInitialized();
    } catch (error) {
      this.showChartError(undefined, 'Chart initialization failed. Please refresh the page.');
      return;
    }

    // Double-check chart exists after initialization
    if (!this.chart) {
      this.showChartError(undefined, 'Failed to update chart - not initialized. Please try again.');
      Log.s('Chart still not initialized after ensureChartInitialized', 'error', {
        chart: this.CHART_ID,
      });
      return;
    }

    // Chart is ready, update immediately
    this.chart.update(valueToSet, true, true, true);

    // Update chart size after DOM updates to ensure logo repositions correctly
    this.updateChartSizeForSubtitle();
  }

  // Usually classes should use updateChart, but in some cases the classes might truly need to add a
  // new series - specifically when the chart has two different axes/types of data or when we're doing battle charts
  protected async addSeriesToChart(series: any) {
    this.hideLoading();
    this.hideChartError();
    this.hideNoData();

    // Ensure chart is initialized before adding series
    try {
      await this.ensureChartInitialized();
    } catch (error) {
      this.showChartError(undefined, 'Chart initialization failed. Please refresh the page.');
      return;
    }

    // Double-check chart exists after initialization
    if (!this.chart) {
      this.showChartError(
        undefined,
        'Failed to add series - chart not initialized. Please try again.'
      );
      return;
    }

    this.chart.addSeries(series, true, true);
    this.chart.update({ subtitle: this.getSubtitleOptions() });

    // Update chart size after DOM updates to ensure logo repositions correctly
    this.updateChartSizeForSubtitle();
  }

  protected getDefaultDateRange(): string[] {
    return [
      moment().subtract(2, 'year').startOf('month').format('YYYY-MM'),
      moment().startOf('month').format('YYYY-MM'),
    ];
  }

  getFieldDisplayName(field: string): string {
    return getPeopleColumnByName(field).displayName;
  }

  private _lastSubtitleLineCount: number = 0;

  protected getRelevantSubtitleFilters(): UIFilter[] {
    const fGroup = QuickBuildService.findFilterGroupById(
      'moneyball',
      this.quickBuild?.rootFilterGroup
    );

    if (!fGroup) return [];

    return fGroup.filters.filter((f): f is UIFilter => {
      if (!QuickBuildService.isUIFilter(f)) return false;
      if (f.filter.field === 'jobs_search') return false;
      return true;
    });
  }

  protected buildSubtitleText(): string {
    const filters = this.getRelevantSubtitleFilters();
    return filters
      .map((f) => {
        if (QuickBuildService.isSubFiltersFilter(f.filter)) {
          return `<strong>${this.getFieldDisplayName(f.filter.field!)}</strong>: ${f.filter.name}`;
        } else {
          return `<strong>${this.getFieldDisplayName(f.filter.field!)}</strong>: ${f.filter.string_values!.join(', ')}`;
        }
      })
      .join(' <br/>');
  }

  protected getSubtitleOptions(): Highcharts.SubtitleOptions {
    return {
      text: this.buildSubtitleText(),
      useHTML: true,
      style: { color: '#ddd' },
    };
  }

  protected updateChartSizeForSubtitle(): void {
    const subtitleText = this.buildSubtitleText();
    if (subtitleText && this.chart) {
      setTimeout(() => {
        if (this.chart) {
          const container = document.getElementById(`highchartsContainer-${this.chartId}`);
          if (container && container.parentElement) {
            const newHeight = container.parentElement.offsetHeight;
            this.chart.setSize(undefined, newHeight, true);
          }
        }
      }, 50);
    }
  }

  protected getSubtitleLineCount(): number {
    const lineCount = this.getRelevantSubtitleFilters().length;

    // If subtitle line count changed, update chart size after next render
    if (lineCount !== this._lastSubtitleLineCount && this.chart) {
      this._lastSubtitleLineCount = lineCount;
      this.updateChartSizeForSubtitle();
    }

    return lineCount;
  }

  protected getChartContainerHeight(customBaseHeight?: number): string {
    const subtitleLines = this.getSubtitleLineCount();
    const baseHeight = customBaseHeight || 400;
    const heightPerLine = 24; // Adjust based on subtitle font size and spacing
    return `${baseHeight + subtitleLines * heightPerLine}px`;
  }

  protected jsonToCsv(jsonArray: any[]): string {
    if (!jsonArray || jsonArray.length === 0) {
      return '';
    }

    // Get all keys from the first object
    const headers = Object.keys(jsonArray[0]);

    // Build the CSV header row
    const csvRows = [headers.join(',')];

    // Build each data row
    for (const obj of jsonArray) {
      const values = headers.map((header) => {
        // Handle CSV escaping for commas, quotes, etc.
        let val = '';
        try {
          val = JSON.stringify(obj[header]);
        } catch (e) {
          val = String(obj[header] ?? '');
        }
        // Escape double quotes
        return `"${val.replace(/"/g, '""')}"`;
      });
      csvRows.push(values.join(','));
    }

    return csvRows.join('\n');
  }

  protected initializeContextMenu() {
    if (!this.chart) return;
    Log.d('BaseReportComponent initializeContextMenu', this.chart);

    this.hideContextMenu();

    const pointerCursorExperiment = this.statsigService.getExperiment('chart_cursor_test');
    const pointerCursor = (pointerCursorExperiment?.get('cursor_type') as string) || 'pointer';

    // Set up point click events in chart options
    this.chart.update({
      tooltip: {
        footerFormat: `
          <div class="tw-flex tw-flex-row tw-justify-start tw-items-center tw-bg-white tw-pt-1 tw-w-full tw-border-0 tw-border-t tw-border-gray-300 tw-border-solid tw-mt-2">
            <span class="tw-flex tw-mr-2">
              <span class="material-icons tw-text-gray-500 tw-text-md tw-text-[20px]">account_circle</span>
            </span>
            <span class="tw-text-sm tw-text-gray-900">
              Click for People Data
            </span>
          </div>
        `,
      },
      plotOptions: {
        bar: {
          cursor: pointerCursor,
        },
        series: {
          cursor: pointerCursor,
          point: {
            events: {
              click: (e: Highcharts.PointClickEventObject) => {
                try {
                  Log.d('click', e);
                  e.preventDefault();
                  e.stopPropagation();
                  this.showContextMenu(e, e.point);
                } catch (error) {
                  Log.s('Error handling chart click', 'error', {
                    error: error?.toString() || 'Unknown error',
                    chart: this.CHART_ID,
                  });
                  this.hideContextMenu();
                }
              },
            },
          },
        },
      },
    });

    // Only add the document click listener if it hasn't been added yet
    if (!this.documentClickSubscription) {
      this.documentClickSubscription = fromEvent<MouseEvent>(document, 'click').subscribe((e) => {
        try {
          // Only hide if we have a menu and the click is outside of it
          if (this.contextMenu && !this.contextMenu.contains(e.target as Node)) {
            this.hideContextMenu();
          }
        } catch (error) {
          Log.s('Error handling document click', 'error', {
            error: error?.toString() || 'Unknown error',
            chart: this.CHART_ID,
          });
          this.hideContextMenu();
        }
      });
    }
  }

  protected showContextMenu(e: MouseEvent, point: Highcharts.Point) {
    this.hideContextMenu();
    let menuItems: ChartContextMenuItem[] = [];

    try {
      menuItems = this.getContextMenuItems(point);
    } catch (error) {
      Log.s('Error getting context menu items', 'error', {
        error: error?.toString() || 'Unknown error',
        chart: this.CHART_ID,
      });
      return;
    }

    if (!menuItems.length) return;

    const isFullscreen = this.chart.fullscreen.isOpen;
    const hasOnlyOneItem = menuItems.length === 1 && menuItems[0].action;

    // If we're in fullscreen, our context menu won't show, so just go to the first action
    if (isFullscreen || hasOnlyOneItem) {
      if (menuItems[0].action) {
        menuItems[0].action(point);
        this.chart.fullscreen.close();
      }
      return;
    }

    const menu = document.createElement('div');
    menu.className = 'highcharts-context-menu';
    menu.style.left = `${e.pageX}px`;
    menu.style.top = `${e.pageY}px`;

    // Add mouseleave event to hide menu when mouse leaves
    menu.addEventListener('mouseleave', () => {
      this.hideContextMenu();
    });

    menuItems.forEach((item) => {
      if (item.divider) {
        const divider = document.createElement('div');
        divider.style.height = '1px';
        divider.style.backgroundColor = '#ccc';
        divider.style.margin = '4px 0';
        menu.appendChild(divider);
        return;
      }

      if (!item.label || !item.action) return;

      const menuItem = document.createElement('div');
      menuItem.className = 'highcharts-context-menu-item';

      if (item.icon) {
        const icon = document.createElement('span');
        icon.className = 'material-icons';
        icon.textContent = item.icon;
        menuItem.appendChild(icon);
      }

      const label = document.createElement('span');
      label.textContent = item.label;
      menuItem.appendChild(label);

      if (!item.disabled) {
        const action = item.action as (point: Highcharts.Point) => void;
        menuItem.addEventListener('click', () => {
          action(point);
          this.hideContextMenu();
          this.chart.fullscreen.close();
        });
      }

      menu.appendChild(menuItem);
    });

    document.body.appendChild(menu);
    this.contextMenu = menu;
  }

  protected hideContextMenu() {
    if (this.contextMenu) {
      this.contextMenu.remove();
      this.contextMenu = null;
    }
  }

  protected openDrawer(qb: QuickBuild) {
    Log.d('job-starts-and-ends-component openDrawer', qb);
    if ((window as any).Intercom) {
      (window as any).Intercom('trackEvent', 'people-drawer-opened');
    }
    this.statsigService.logEvent('wfinsights-people-drawer-opened', 1, {
      chart_type: this.CHART_ID,
    });

    HeapUtils.track('people_drawer_opened', {
      chart_type: this.CHART_ID,
    });

    this.streamsDrawerService.openDrawer({
      quickBuild: qb,
      title: qb.name,
    });
  }

  // Configuration management methods
  protected registerConfigProperty<K extends keyof TConfig>(
    propertyName: K,
    getter: () => TConfig[K]
  ): void {
    this.configProperties.set(propertyName as string, getter);
  }

  protected emitConfiguration(): void {
    // This is called when chart options change, so we hook in here to hide the options when changed when in mobile
    if (this.layoutService.isSmallScreen) {
      this.showChartOptions = false;
    }

    const config: Partial<TConfig> = {};

    for (const [propertyName, getter] of this.configProperties) {
      config[propertyName as keyof TConfig] = getter();
    }

    this._config = config;
    this.configChange.emit(config as TConfig);
  }

  protected getConfiguration(): Partial<TConfig> {
    return this._config;
  }
  //-- Configuration management methods

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    if (this.documentClickSubscription) {
      this.documentClickSubscription.unsubscribe();
    }
    if (this.layoutSubscription) {
      this.layoutSubscription.unsubscribe();
    }
    this.hideContextMenu();
    this.hideChartError();
    this.hideNoData();
    this.hideLoading();
  }
}
